import React, {Component} from 'react';
import PropTypes from 'prop-types';

import {withStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';

import {http, api} from '../common/common';
import ViewStack from './ViewStack';

const styles = (theme) => {
    return {
        paper: {
            position: 'relative',
            width: 32,
            padding: 0,
            borderColor: theme.palette.primary.mainBorder,
            borderWidth: 1,
            borderStyle: 'solid',
            backgroundColor: theme.palette.primary.main,
        },
        bar: {
            width: 16,
            height: 140,
            marginLeft: 'auto',
            marginRight: 'auto',
        },
        block: {
            width: 16,
            height: 26,
            marginLeft: 'auto',
            marginRight: 'auto',
            borderColor: theme.palette.primary.mainBorder,
            borderWidth: 1,
            borderStyle: 'solid',
            backgroundColor: theme.palette.primary.main,
        },
        label: {
            color: theme.palette.primary.displayText,
            fontSize: 10,
            marginTop: 6,
            marginBottom: 6,
        },
    };
}

class GraphLegendControl extends Component {
    constructor(props) {
        super(props);

        this.state = {
            open: false,
            timestamp: 0,
        }

        this._cancelToken = http.CancelToken.source();
        this._colorBlock = null;
    }

    shouldComponentUpdate = (nextProps, nextState) => {
        const nextView = nextProps.viewStack.peek();

        if (!nextView.layer) {
            return false;
        }

        const nextUid = nextView.uid;
        const [nextLayerType, nextLayerIndex] = nextView.layerSelection;

        if (this._uid !== nextUid ||
            this._layerType !== nextLayerType ||
            this._layerIndex !== nextLayerIndex) {
            this._colorBlock = null;
        }

        return (this._uid !== nextUid ||
            this._layerType !== nextLayerType ||
            this._layerIndex !== nextLayerIndex ||
            this.state.open !== nextState.open ||
            this.state.timestamp !== nextState.timestamp);
    }

    componentDidUpdate = (prevProps, prevState) => {
        const nextView = this.props.viewStack.peek();

        if (!nextView.layer) {
            return false;
        }

        const nextUid = nextView.uid;
        const [nextLayerType, nextLayerIndex] = nextView.layerSelection;

        if (this._uid !== nextUid ||
            this._layerType !== nextLayerType ||
            this._layerIndex !== nextLayerIndex) {

            this._uid = nextUid;
            this._layerType = nextLayerType;
            this._layerIndex = nextLayerIndex;
            this._update();
        }
    }

    componentWillUnmount = () => {
        if (this._cancelToken) {
            this._cancelToken.cancel();
        }
    }

    render = () => {
        if (!this.state.open) {
            return null;
        }

        if (!this._colorBlock) {
            return null;
        }

        const {classes} = this.props;

        const topLabel = this._colorBlock[0].label ? this._colorBlock[0].label : "Good";
        const lastIndex = this._colorBlock.length - 1;
        const bottomLabel = this._colorBlock[lastIndex].label ? this._colorBlock[lastIndex].label : "Poor";

        return (
            <div className={this.props.className}>
                <Paper
                    className={classes.paper}
                    square={false}
                >
                    <Typography className={classes.label}>
                        {topLabel}
                    </Typography>
                    {this._renderColorBar(false)}
                    <Typography className={classes.label}>
                        {bottomLabel}
                    </Typography>
                </Paper>
            </div>
        );
    }

    _update = () => {
        if (this.props.viewStack.isDefaultBase()) {
            this.setState({
                open: false,
            });

            return;
        }

        const view = this.props.viewStack.peek();
        const selectedLayerData = view.selectedLayerData;

        const hasData = (!this.props.viewStack.isDefaultBase() && selectedLayerData &&
            (selectedLayerData.legend || selectedLayerData.graph));

        if (hasData) {
            this._requestLegend(view);
        } else {
            this.setState({
                open: false,
            });
        }
    }

    _renderColorBar = (continous) => {
        const {classes} = this.props;

        if (continous) {
            const style = {
                backgroundImage: this._generateGradient()
            };

            return (
                <div
                    className={classes.bar}
                    style={style}
                />
            );
        } else {
            return this._colorBlock.map(this._renderColorBlock);
        }
    }

    _renderColorBlock = (data, index, arr) => {
        const {classes} = this.props;

        const style = {
            backgroundColor: this._resetOpacity(data.color, 0.87),
        }

        return (
            <div
                className={classes.block}
                key={index}
                style={style}
            />
        )
    }

    _resetOpacity = (hex, opacity) => {
        hex = hex.replace('#','');
        const r = parseInt(hex.substring(0,2), 16);
        const g = parseInt(hex.substring(2,4), 16);
        const b = parseInt(hex.substring(4,6), 16);

        return 'rgba(' + r + ',' + g + ',' + b + ',' + opacity + ')';
    }

    _generateGradient = () => {
        const temp = this._colorBlock.map((data, index, arr) => {
            return this._resetOpacity(data.color, 1.0);
        });

        return 'linear-gradient(' + temp.join(',') + ')';
    }

    _needDisplayLegend = (colorBlock) => {
        const length = colorBlock.length;

        if (length === 0) {
            return false;
        }

        if (length <= 2) {
            return false;
        }

        var labelCount = 0;

        for (var i = 1; i < length - 1; ++i) {
            if (colorBlock[i].label) {
                ++labelCount;
            }
        }

        return (labelCount === 0)
    }

    _requestLegend = async (view) => {
        try {
            const response = await http.get(api.map() + '/legend', {
                params: {
                    'access_code': this.props.getAccessCode(),
                    'organization': view.organization,
                    'region': view.key,
                    'layer_type': view.selectedLayerType,
                    'layer_key': view.selectedLayerData.originalIndexKey,
                },
                cancelToken: this._cancelToken.token,
            });

            if (response.data.bin) {
                this._colorBlock = response.data.bin;
                this._colorBlock.reverse();

                const needDisplay = this._needDisplayLegend(this._colorBlock);

                if (!needDisplay) {
                    this._colorBlock = [];
                }

                this.setState({
                    open: needDisplay,
                    timestamp: Date.now(),
                });
            }
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            this.setState({
                open: false,
                timestamp: Date.now(),
            });
        }
    }
}

GraphLegendControl.propTypes = {
    getAccessCode: PropTypes.func.isRequired,
    viewStack: PropTypes.instanceOf(ViewStack).isRequired,
}

export default withStyles(styles)(GraphLegendControl);
